import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { collection, query, where, getDocs, deleteDoc, doc, getDoc } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { db, auth } from '../../lib/firebase';
import { useSalePage } from '../../contexts/SalePageContext';
import { useAuthContext } from '../../contexts/AuthContext';
// @ts-ignore - Vite raw import of the design markup exported from Claude Design
import dashboardHtml from './saledi-dashboard.html?raw';

interface SalePage {
  id: string; userId: string; title: string; slug: string; url: string;
  isPublished: boolean;
  views: { today: number; week: number; month: number; total: number };
}

const PERIOD_LABEL: Record<string, string> = { today: 'วันนี้', week: 'สัปดาห์', month: 'เดือน' };

/** SaleDi dashboard — implemented from Claude Design "Dashboard.dc.html", wired to real data. */
export default function SalePageDashboard() {
  const navigate = useNavigate();
  const { subscription, isSubscriptionExpired } = useSalePage() as any;
  const { user } = useAuthContext();
  const rootRef = useRef<HTMLDivElement>(null);
  const [salePages, setSalePages] = useState<SalePage[]>([]);
  const [loaded, setLoaded] = useState(false);

  const isExpired = !!(typeof isSubscriptionExpired === 'function' ? isSubscriptionExpired() : isSubscriptionExpired);

  // fetch the user's real sale pages
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!user?.uid) { setLoaded(true); return; }
      try {
        const snap = await getDocs(query(collection(db, 'salepages'), where('userId', '==', user.uid)));
        const pages: SalePage[] = snap.docs.map(d => {
          const data: any = d.data();
          let url = '';
          if (subscription?.hasSubdomain && subscription?.username) url = `${subscription.username}.edumi.co/sale`;
          else if (subscription?.hasPathUrl && subscription?.username) url = `edumi.co/@${subscription.username}/sale`;
          else url = `saledi.web.app/s/${data.slug}`;
          return { id: d.id, userId: data.userId, title: data.title || 'Sale Page', slug: data.slug, url, isPublished: data.isPublished !== false, views: data.views || { today: 0, week: 0, month: 0, total: 0 } };
        });
        if (alive) { setSalePages(pages); setLoaded(true); }
      } catch (e) { console.error('[Dashboard] fetch salepages', e); if (alive) setLoaded(true); }
    })();
    return () => { alive = false; };
  }, [user?.uid, subscription?.hasSubdomain, subscription?.hasPathUrl, subscription?.username]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const R = rootRef.current;
    if (!R) return;
    const cleanups: Array<() => void> = [];
    const on = (el: any, ev: string, fn: any) => { if (!el) return; el.addEventListener(ev, fn); cleanups.push(() => el.removeEventListener(ev, fn)); };
    const period = { v: 'today' as 'today' | 'week' | 'month' };

    // ---- lucide icons (CDN, same as the design) ----
    const drawIcons = () => { try { (window as any).lucide?.createIcons(); } catch { /* ignore */ } };
    const ensureLucide = () => {
      if ((window as any).lucide) { drawIcons(); return; }
      let s = document.getElementById('lucide-cdn') as HTMLScriptElement | null;
      if (!s) { s = document.createElement('script'); s.id = 'lucide-cdn'; s.src = 'https://unpkg.com/lucide@latest'; document.head.appendChild(s); }
      s.addEventListener('load', drawIcons);
    };

    // ---- period tabs + total views ----
    const tabStyle = (a: boolean) => "font-family:'Anuphan',sans-serif; font-size:13.5px; font-weight:500; padding:8px 16px; border-radius:999px; border:0; cursor:pointer; background:" + (a ? "#0E3A40; color:#fff" : "transparent; color:#5B7D80");
    const tabs: Record<string, HTMLElement | null> = { today: R.querySelector('#tabToday'), week: R.querySelector('#tabWeek'), month: R.querySelector('#tabMonth') };
    const renderPeriod = () => {
      (['today', 'week', 'month'] as const).forEach(k => tabs[k]?.setAttribute('style', tabStyle(period.v === k)));
      const total = salePages.reduce((s, p) => s + (p.views?.[period.v] || 0), 0);
      const tv = R.querySelector('#totalViews'); if (tv) tv.textContent = String(total);
      const pl = R.querySelector('#periodLabel'); if (pl) pl.textContent = PERIOD_LABEL[period.v];
    };
    (['today', 'week', 'month'] as const).forEach(k => on(tabs[k], 'click', () => { period.v = k; renderPeriod(); }));

    // ---- create / edit / delete ----
    const guard = (): boolean => {
      if (subscription?.status === 'pending_email_verification') { alert('กรุณายืนยันอีเมลก่อนใช้งานฟีเจอร์นี้'); return false; }
      // Free users (no package yet) may edit their draft; only truly-expired must renew.
      if (subscription && isExpired) { alert('แพ็คเก็จหมดอายุแล้ว กรุณาต่ออายุเพื่อใช้งาน'); navigate('/salepage/renew'); return false; }
      return true;
    };
    const handleCreate = () => {
      if (subscription?.status === 'pending_email_verification') { alert('กรุณายืนยันอีเมลก่อนใช้งานฟีเจอร์นี้'); return; }
      // Freemium: first Sale Page is free to build as a draft. Extra pages need a package.
      if (salePages.length >= 1) { navigate('/salepage/renew'); return; } // reached 1-page limit → upsell (buy extra page)
      navigate('/sale-page');
    };
    on(R.querySelector('#addPageBtn'), 'click', handleCreate);

    // ---- build the real pages list ----
    const list = R.querySelector('#pagesList');
    if (list) {
      if (!loaded) {
        list.innerHTML = '<div style="text-align:center; padding:34px; color:#8AA6A8">กำลังโหลด...</div>';
      } else if (salePages.length === 0) {
        list.innerHTML = '<div style="text-align:center; padding:36px 20px; border:1px dashed #CFE0E0; border-radius:18px"><div style="font-family:\'Anuphan\',sans-serif; font-weight:600; color:#0E3A40; margin-bottom:6px">ยังไม่มี Sale Page</div><div style="font-size:13.5px; color:#5B7D80; margin-bottom:16px">สร้างหน้าขายแรกของคุณได้เลย</div><button type="button" id="emptyCreate" style="display:inline-flex; align-items:center; gap:7px; background:linear-gradient(120deg,#00B4A1,#3A93B9); color:#fff; font-family:\'Anuphan\',sans-serif; font-weight:600; font-size:14px; padding:11px 20px; border-radius:999px; border:0; cursor:pointer">+ สร้าง Sale Page</button></div>';
        on(list.querySelector('#emptyCreate'), 'click', handleCreate);
      } else {
        const stat = (label: string, icon: string, val: number, bg: string, col: string) =>
          '<div style="background:' + bg + '; border-radius:14px; padding:12px 14px"><div style="font-size:12.5px; font-weight:500; color:' + col + '; display:flex; align-items:center; gap:5px; margin-bottom:6px"><i data-lucide="' + icon + '" style="width:13px; height:13px"></i> ' + label + '</div><div style="font-family:\'Anuphan\',sans-serif; font-weight:700; font-size:19px">' + val + '</div></div>';
        const badge = (pub: boolean) => pub
          ? '<span style="font-size:11px; font-weight:600; padding:3px 9px; border-radius:999px; background:rgba(0,180,161,0.12); color:#0E7C86; white-space:nowrap">เผยแพร่แล้ว</span>'
          : '<span style="font-size:11px; font-weight:600; padding:3px 9px; border-radius:999px; background:rgba(255,122,69,0.12); color:#B3552A; white-space:nowrap">ยังไม่เผยแพร่</span>';
        list.innerHTML = salePages.map(p =>
          '<div style="border:1px solid #EAF1F1; border-radius:18px; padding:clamp(16px,2vw,20px); margin-bottom:12px">' +
            '<div style="display:flex; flex-wrap:wrap; align-items:flex-start; justify-content:space-between; gap:10px; margin-bottom:14px">' +
              '<div style="min-width:0"><div style="display:flex; align-items:center; gap:8px; flex-wrap:wrap; margin-bottom:3px"><span style="font-family:\'Anuphan\',sans-serif; font-weight:600; font-size:16.5px">' + p.title + '</span>' + badge(p.isPublished) + '</div>' +
              (p.isPublished
                ? '<a href="https://' + p.url + '" target="_blank" rel="noopener" style="font-size:13.5px; display:inline-flex; align-items:center; gap:5px">' + p.url + ' <i data-lucide="external-link" style="width:12px; height:12px"></i></a>'
                : '<span style="font-size:13px; color:#8A4A26">หากต้องการเผยแพร่ กรุณาสมัครแพ็คเกจเริ่มต้น</span>') + '</div>' +
              '<div style="display:flex; gap:8px; flex-wrap:wrap">' +
                (p.isPublished ? '' : '<button type="button" data-publish="' + p.id + '" style="display:inline-flex; align-items:center; gap:6px; height:38px; padding:0 18px; border-radius:11px; border:0; background:linear-gradient(120deg,#FF7A45,#E85D2A); color:#fff; font-family:\'Anuphan\',sans-serif; font-weight:600; font-size:13.5px; cursor:pointer; white-space:nowrap"><i data-lucide="rocket" style="width:15px; height:15px"></i> คลิกเลย</button>') +
                '<button type="button" data-edit="' + p.id + '" style="width:38px; height:38px; border-radius:11px; border:0; background:#00B4A1; color:#fff; display:flex; align-items:center; justify-content:center; cursor:pointer"><i data-lucide="pencil" style="width:15px; height:15px"></i></button>' +
                '<button type="button" data-del="' + p.id + '" style="width:38px; height:38px; border-radius:11px; border:1px solid #EAF1F1; background:#fff; color:#8AA6A8; display:flex; align-items:center; justify-content:center; cursor:pointer"><i data-lucide="trash-2" style="width:15px; height:15px"></i></button>' +
              '</div>' +
            '</div>' +
            '<div style="display:grid; grid-template-columns:repeat(auto-fit,minmax(min(100%,150px),1fr)); gap:10px">' +
              stat('วันนี้', 'eye', p.views.today, 'rgba(0,180,161,0.1)', '#0E7C86') +
              stat('สัปดาห์', 'calendar-days', p.views.week, 'rgba(58,147,185,0.1)', '#276E8A') +
              stat('เดือน', 'trending-up', p.views.month, 'rgba(91,111,203,0.1)', '#4A57A8') +
            '</div>' +
          '</div>'
        ).join('');
        // Publish a draft → go choose a package & pay; activateSalePageFromSlip publishes it automatically
        list.querySelectorAll('[data-publish]').forEach(b => on(b, 'click', () => { navigate('/salepage/renew'); }));
        list.querySelectorAll('[data-edit]').forEach(b => on(b, 'click', () => { if (!guard()) return; navigate('/sale-page?salePageId=' + (b as HTMLElement).dataset.edit); }));
        list.querySelectorAll('[data-del]').forEach(b => on(b, 'click', async () => {
          if (!guard()) return;
          const id = (b as HTMLElement).dataset.del!;
          if (!confirm('ต้องการลบ Sale Page นี้หรือไม่?')) return;
          try { await deleteDoc(doc(db, 'salepages', id)); setSalePages(prev => prev.filter(x => x.id !== id)); }
          catch (e) { console.error(e); alert('เกิดข้อผิดพลาดในการลบ Sale Page'); }
        }));
      }
    }

    // ---- header: plan badge, banner, account/feature menus ----
    const name = user?.displayName || (user as any)?.username || (user?.email || '').split('@')[0] || 'ผู้ใช้';
    const setText = (sel: string, t: string) => { const el = R.querySelector(sel); if (el) el.textContent = t; };
    setText('#acctName', name);
    setText('#acctEmail', user?.email || '');
    // load the user's profile image into the account avatar (like the shared navbar does)
    if (user?.uid) {
      getDoc(doc(db, 'users', user.uid)).then(s => {
        const img = s.exists() ? (s.data().profileImage || '') : '';
        const av = R.querySelector<HTMLElement>('#acctAvatar');
        if (av && img) av.innerHTML = '<img src="' + img + '" alt="" style="width:100%; height:100%; object-fit:cover" onerror="this.parentElement.innerHTML=\'\'" />';
      }).catch(() => { /* ignore */ });
    }
    const planLabel = (p?: string) => p === 'basic' ? 'Basic' : p === 'premium' ? 'Premium' : p === 'premium_vip' ? 'Premium VIP' : p === 'free' ? 'ทดสอบระบบ' : (p ? String(p).toUpperCase() : '');
    const planText = subscription?.plan ? planLabel(subscription.plan) : (subscription ? 'สมาชิก' : 'ทดสอบระบบ');
    setText('#planBadgeText', planText);
    // banner — free users (no package yet) are in test mode, not "expired"
    if (subscription && isExpired) { setText('#bannerTitle', 'แพ็คเกจหมดอายุ'); setText('#bannerText', 'ต่ออายุเพื่อเผยแพร่ Sale Page ต่อ'); }
    else if (subscription?.status === 'trial') { setText('#bannerTitle', 'กำลังทดลองใช้งาน'); setText('#bannerText', 'สร้างและเผยแพร่ Sale Page ได้เต็มรูปแบบ'); }
    else if (subscription?.plan) { setText('#bannerTitle', 'แพ็คเกจ ' + planText); setText('#bannerText', 'จัดการ Sale Page ของคุณได้ที่นี่'); }
    else { setText('#bannerTitle', 'โหมดทดสอบระบบ'); setText('#bannerText', 'ทดลองสร้างหน้าของคุณได้เลย — หากต้องการเผยแพร่ กรุณาสมัครแพ็คเกจเริ่มต้น'); }

    // dropdown menus
    const featBtn = R.querySelector<HTMLElement>('#featBtn'); const featMenu = R.querySelector<HTMLElement>('#featMenu');
    const acctBtn = R.querySelector<HTMLElement>('#acctBtn'); const acctMenu = R.querySelector<HTMLElement>('#acctMenu');
    const closeMenus = () => { if (featMenu) featMenu.style.display = 'none'; if (acctMenu) acctMenu.style.display = 'none'; };
    on(featBtn, 'click', (e: Event) => { e.stopPropagation(); const open = featMenu!.style.display !== 'none'; closeMenus(); if (featMenu) featMenu.style.display = open ? 'none' : 'block'; });
    on(acctBtn, 'click', (e: Event) => { e.stopPropagation(); const open = acctMenu!.style.display !== 'none'; closeMenus(); if (acctMenu) acctMenu.style.display = open ? 'none' : 'block'; });
    on(document, 'click', closeMenus);
    R.querySelectorAll<HTMLElement>('[data-go]').forEach(a => on(a, 'click', (e: Event) => { e.preventDefault(); closeMenus(); navigate(a.dataset.go!); }));
    // ข้อความ/การชำระเงิน require an active subscription (SalePageGuard) — hide when the plan can't use them
    const hasValidSub = !!subscription && (subscription.status === 'active' || subscription.status === 'trial' || subscription.plan === 'pro_plus_yearly');
    if (!hasValidSub) R.querySelectorAll<HTMLElement>('[data-go="/salepage/messages"], [data-go="/salepage/payments"]').forEach(a => { a.style.display = 'none'; });
    R.querySelectorAll<HTMLElement>('[data-nav="home"]').forEach(a => on(a, 'click', (e: Event) => { e.preventDefault(); navigate('/dashboard'); }));
    on(R.querySelector('#logoutBtn'), 'click', async (e: Event) => { e.preventDefault(); try { localStorage.removeItem('auth_user_cache'); await signOut(auth); } catch { /* ignore */ } navigate('/'); });

    renderPeriod();
    ensureLucide();
    return () => { cleanups.forEach(fn => fn()); };
  }, [navigate, user?.uid, user?.email, subscription?.status, subscription?.plan, salePages, loaded, isExpired]);

  return (
    <>
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link href="https://fonts.googleapis.com/css2?family=Anuphan:wght@400;500;600;700&family=IBM+Plex+Sans+Thai:wght@400;500;600&display=swap" rel="stylesheet" />
      </Helmet>
      <style>{`
        #saledi-dash{ background:#F3F8F8; color:#0E3A40; font-family:'IBM Plex Sans Thai', system-ui, sans-serif; -webkit-font-smoothing:antialiased; min-height:100vh; }
        #saledi-dash a{ color:#3A93B9; text-decoration:none; }
        #saledi-dash a:hover{ color:#0E7C86; }
        #saledi-dash [data-go]:hover, #saledi-dash #logoutBtn:hover{ background:#F3F8F8; }
      `}</style>
      <div id="saledi-dash" ref={rootRef} dangerouslySetInnerHTML={{ __html: dashboardHtml }} />
    </>
  );
}
